import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { ChildSelectorBar } from '../components/ChildSelectorBar';
import { Lightbulb, Clock, Target, Info, ArrowRight, Star, Zap, History, X } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { DjayNarrator } from '../../../mascot';
import { MODULES } from '../../../content';
import { getWeeklyActivityForLearner } from '../../../utils/activityHelpers';
import { getPracticeDaysThisWeek } from '../../../utils/streakFacts';
import { type LearnerAccount, type ParentAccount } from '../../../accounts';
import { AppState } from '../../../app/types';
import { ParentV2Route } from '../ParentSidebarV2';
import { useChildDecay } from '../hooks/useChildDecay';
import { useModuleMastery } from '../hooks/useModuleMastery';
import { shuffleWithSeed } from '../../../utils/deterministicShuffle';
import { getParentTipDismissals, setParentTipDismissed, clearParentTipDismissal, isTipDismissed } from '../../../data/domains/preferences';
interface TipsPageProps {
  activeAccount: ParentAccount;
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number;
  setViewingLearner: (id: string) => void;
  onNavigate?: (route: ParentV2Route) => void;
}
type TipColor = 'amber' | 'indigo' | 'emerald' | 'rose' | 'sky';
interface GeneratedTip {
  id: string;
  icon: React.ReactNode;
  title: string;
  content: string;
  color: TipColor;
  /** When true, the tip cannot be dismissed (e.g. permanent platform info). */
  pinned?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}
/**
 * ISO-week-of-year helper (deterministic shuffle seed for W4-2).
 * Same week → same order; next week → new order without backend.
 */
function getISOWeekOfYear(date: Date = new Date()): number {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNr = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNr + 3);
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  const diff = (target.getTime() - firstThursday.getTime()) / (24 * 60 * 60 * 1000);
  return 1 + Math.round((diff - 3 + (firstThursday.getUTCDay() + 6) % 7) / 7);
}
export function TipsPage({
  activeAccount,
  linkedLearners,
  selectedChild,
  childData,
  getLearnerModuleMastery,
  setViewingLearner,
  onNavigate
}: TipsPageProps) {
  const decay = useChildDecay(childData);
  const moduleMastery = useModuleMastery(selectedChild, getLearnerModuleMastery);
  // W4-2: parent-scoped dismissals. Loaded async; surface a stable empty
  // map first to avoid flashing dismissed cards in.
  const [dismissals, setDismissals] = useState<Record<string, number>>({});
  useEffect(() => {
    let cancelled = false;
    void getParentTipDismissals(activeAccount.id).then(next => {
      if (!cancelled) setDismissals(next);
    });
    return () => {
      cancelled = true;
    };
  }, [activeAccount.id]);
  const generatedTips = useMemo<GeneratedTip[]>(() => {
    if (!selectedChild || !childData) return [];
    const tips: GeneratedTip[] = [];
    // 1. Rhythm Tip
    const weeklyActivity = getWeeklyActivityForLearner(childData.questionStates || {});
    const daysPracticed = weeklyActivity.filter(d => d.practiced).length;
    if (daysPracticed === 0) {
      tips.push({
        id: 'rhythm-none',
        icon: <Clock className="w-6 h-6 text-amber-500" data-id="element-8866" data-mp="1erxd6agx0pqz" />,
        title: 'Start met een vast ritme',
        content: `Probeer een vast moment op de dag te vinden voor ${selectedChild.displayName} om te oefenen. Bijvoorbeeld 10 minuten na het eten. Korte, regelmatige sessies werken veel beter dan één lange sessie in het weekend.`,
        color: 'amber'
      });
    } else if (daysPracticed < 3) {
      tips.push({
        id: 'rhythm-some',
        icon: <Clock className="w-6 h-6 text-indigo-500" data-id="element-8867" data-mp="80c8td2dzsxh" />,
        title: 'Bouw het ritme uit',
        content: `${selectedChild.displayName} heeft deze week ${daysPracticed} keer geoefend. Dat is een mooi begin! Probeer toe te werken naar 3 of 4 keer per week voor het beste leereffect.`,
        color: 'indigo'
      });
    } else {
      tips.push({
        id: 'rhythm-good',
        icon: <Clock className="w-6 h-6 text-emerald-500" data-id="element-8868" data-mp="qv92vvzmjtrn" />,
        title: 'Houd dit ritme vast!',
        content: `Fantastisch! ${selectedChild.displayName} oefent heel regelmatig. Dit is de perfecte manier om nieuwe stof eigen te maken en te onthouden. Blijf dit stimuleren.`,
        color: 'emerald'
      });
    }
    // 2. Weakest module
    const strongest = moduleMastery[0] ?? null;
    const weakest = moduleMastery.length > 0 ? moduleMastery[moduleMastery.length - 1] : null;
    const weakestModule = weakest?.module ?? null;
    const lowestMastery = weakest?.mastery ?? 100;
    const strongestModule = strongest?.module ?? null;
    const highestMastery = strongest?.mastery ?? 0;
    if (weakestModule && lowestMastery < 50) {
      tips.push({
        id: `subject-weak-${weakestModule.id}`,
        icon: <Target className="w-6 h-6 text-rose-500" data-id="element-8869" data-mp="1eeqhsnny1jvk" />,
        title: `Extra aandacht voor ${weakestModule.title.toLowerCase()}`,
        content: `Het vak ${weakestModule.title.toLowerCase()} is nog wat lastig (${lowestMastery}% beheersing). Meester djay zal hier automatisch makkelijkere vragen over stellen om het vertrouwen op te bouwen. Je kunt helpen door in het dagelijks leven samen sommetjes of taalpuzzels te maken die hierbij passen.`,
        color: 'rose',
        action: {
          label: 'Bekijk voortgang',
          onClick: () => onNavigate?.('voortgang')
        }
      });
    }
    // 3. Strongest module praise
    if (strongestModule && highestMastery >= 70) {
      tips.push({
        id: `subject-strong-${strongestModule.id}`,
        icon: <Star className="w-6 h-6 text-emerald-500" data-id="element-8870" data-mp="2ta8k8jp0qvcf" />,
        title: `Uitblinker in ${strongestModule.title.toLowerCase()}!`,
        content: `${selectedChild.displayName} gaat ontzettend goed met ${strongestModule.title.toLowerCase()} (${highestMastery}% beheersing). Geef een complimentje! Succeservaringen zijn heel belangrijk voor de motivatie.`,
        color: 'emerald'
      });
    }
    // 4. Ritme deze week.
    //
    // Las eerst `stats.streakState.currentStreak` — een pad dat op UserStats niet
    // bestaat, dus altijd 0. Deze tip kon daardoor NOOIT verschijnen: `|| 0` maakt
    // een ontbrekend veld niet te onderscheiden van een echte nul.
    const currentStreak = getPracticeDaysThisWeek(childData);
    if (currentStreak >= 3) {
      tips.push({
        id: 'streak-active',
        icon: <Zap className="w-6 h-6 text-amber-500" data-id="element-8871" data-mp="1qwjgmra95chq" />,
        title: `${currentStreak} dagen op rij geoefend!`,
        content: `Wat een inzet! ${selectedChild.displayName} heeft een 'streak' van ${currentStreak} dagen opgebouwd. Probeer deze reeks niet te verbreken, dat werkt enorm motiverend.`,
        color: 'amber'
      });
    }
    // 5. Mastery decay
    if (childData.familyMastery) {
      const decayingModules: Array<{
        module: (typeof MODULES)[number];
        worstStatus: 'fading' | 'stale';
        daysSince: number;
      }> = [];
      for (const module of MODULES) {
        const mastery = getLearnerModuleMastery(selectedChild.id, module.id);
        if (mastery <= 0) continue;
        const info = decay.perModule[module.id];
        if (!info || info.worstStatus === null) continue;
        decayingModules.push({
          module,
          worstStatus: info.worstStatus,
          daysSince: info.daysSince
        });
      }
      decayingModules.sort((a, b) => {
        if (a.worstStatus !== b.worstStatus) {
          return a.worstStatus === 'stale' ? -1 : 1;
        }
        return b.daysSince - a.daysSince;
      });
      decayingModules.slice(0, 2).forEach(dm => {
        const isStale = dm.worstStatus === 'stale';
        const weeksAgo = Math.floor(dm.daysSince / 7);
        const timeLabel = weeksAgo >= 4 ? `meer dan een maand` : weeksAgo >= 2 ? `${weeksAgo} weken` : `meer dan een week`;
        tips.push({
          id: `mastery-decay-${dm.module.id}`,
          icon: <History className={`w-6 h-6 ${isStale ? 'text-rose-500' : 'text-indigo-500'}`} data-id="element-8872" data-mp="1bdxe0kq83s2g" />,
          title: isStale ? `${dm.module.title} dreigt weg te zakken` : `Tijd voor herhaling: ${dm.module.title.toLowerCase()}`,
          content: isStale ? `${selectedChild.displayName} heeft al ${timeLabel} niet geoefend met ${dm.module.title.toLowerCase()}. De kennis begint weg te zakken. Even 10 minuten oefenen kan al veel helpen!` : `Het is ${timeLabel} geleden dat ${selectedChild.displayName} heeft geoefend met ${dm.module.title.toLowerCase()}. Regelmatig herhalen helpt om de stof goed te onthouden.`,
          color: isStale ? 'rose' : 'indigo',
          action: {
            label: 'Bekijk vakken',
            onClick: () => onNavigate?.('voortgang')
          }
        });
      });
    }
    // 6. Platform info — pinned, cannot be dismissed
    tips.push({
      id: 'platform-info',
      icon: <Info className="w-6 h-6 text-sky-500" data-id="element-8873" data-mp="3czjf3sjxf931" />,
      title: 'Hoe Meester djay werkt',
      content: 'Het systeem past zich automatisch aan het niveau van je kind aan. Als het goed gaat, worden de vragen uitdagender. Bij fouten doet het systeem een stapje terug en geeft het extra uitleg. Fouten maken is hier dus letterlijk onderdeel van het leerproces!',
      color: 'sky',
      pinned: true
    });
    return tips;
  }, [selectedChild, childData, getLearnerModuleMastery, onNavigate, decay.perModule, moduleMastery]);
  // W4-2: filter dismissed (within hide-window) + deterministic week-shuffle.
  // Pinned tip ('platform-info') is always kept and always rendered last.
  const visibleTips = useMemo<GeneratedTip[]>(() => {
    const active = generatedTips.filter(tip => tip.pinned || !isTipDismissed(dismissals, tip.id));
    const pinned = active.filter(t => t.pinned);
    const cyclable = active.filter(t => !t.pinned);
    const weekOfYear = getISOWeekOfYear(new Date());
    const seed = `${activeAccount.id}-${selectedChild?.id ?? 'none'}-w${weekOfYear}`;
    const {
      shuffled
    } = shuffleWithSeed(cyclable, seed);
    return [...shuffled, ...pinned];
  }, [generatedTips, dismissals, activeAccount.id, selectedChild?.id]);
  const handleDismiss = useCallback(async (tip: GeneratedTip) => {
    if (tip.pinned) return;
    const ts = Date.now();
    setDismissals(prev => ({
      ...prev,
      [tip.id]: ts
    }));
    await setParentTipDismissed(activeAccount.id, tip.id, ts);
    toast('Tip verborgen', {
      description: 'Deze tip komt over 14 dagen weer terug.',
      action: {
        label: 'Ongedaan maken',
        onClick: () => {
          setDismissals(prev => {
            const next = {
              ...prev
            };
            delete next[tip.id];
            return next;
          });
          void clearParentTipDismissal(activeAccount.id, tip.id);
        }
      },
      duration: 5000
    });
  }, [activeAccount.id]);
  if (!selectedChild) {
    return <PageLayout title="Tips" description="Persoonlijk advies van Djay" data-id="element-8874" data-mp="2a56yljjgtmqw">
        <div className="max-w-4xl mx-auto pb-12" data-id="element-8875" data-mp="264n0odr2if3p">
          <div className="flex flex-col items-center justify-center py-20 px-4 text-center" data-id="element-8876" data-mp="2k562h34xwoxf">
            <DjayNarrator size="lg" emotion="thinking" animate data-id="element-8877" data-mp="2f74mws6q37qp" />
            <h2 className="text-xl font-bold text-slate-900 mt-6 mb-2" data-id="element-8878" data-mp="2f4ifcsju0s8h">
              Koppel eerst een kind
            </h2>
            <p className="text-slate-500 max-w-md" data-id="element-8879" data-mp="2owo6k029f5mm">
              Om persoonlijke tips te kunnen geven, moet je eerst een kind
              koppelen. Ga naar Instellingen om een kind te koppelen met een
              koppelcode.
            </p>
          </div>
        </div>
      </PageLayout>;
  }
  const colorClasses: Record<TipColor, string> = {
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    rose: 'bg-rose-50 border-rose-200 text-rose-900',
    sky: 'bg-sky-50 border-sky-200 text-sky-900'
  };
  const iconBgClasses: Record<TipColor, string> = {
    amber: 'bg-amber-100',
    indigo: 'bg-indigo-100',
    emerald: 'bg-emerald-100',
    rose: 'bg-rose-100',
    sky: 'bg-sky-100'
  };
  const actionTextClasses: Record<TipColor, string> = {
    amber: 'text-amber-700 hover:text-amber-800',
    indigo: 'text-indigo-700 hover:text-indigo-800',
    emerald: 'text-emerald-700 hover:text-emerald-800',
    rose: 'text-rose-700 hover:text-rose-800',
    sky: 'text-sky-700 hover:text-sky-800'
  };
  const dismissBtnClasses: Record<TipColor, string> = {
    amber: 'text-amber-500 hover:bg-amber-100 hover:text-amber-700',
    indigo: 'text-indigo-500 hover:bg-indigo-100 hover:text-indigo-700',
    emerald: 'text-emerald-500 hover:bg-emerald-100 hover:text-emerald-700',
    rose: 'text-rose-500 hover:bg-rose-100 hover:text-rose-700',
    sky: 'text-sky-500 hover:bg-sky-100 hover:text-sky-700'
  };
  return <PageLayout title="Tips" description="Persoonlijk advies van Djay" data-id="element-8880" data-mp="gn50k8dg4adi">
      <div className="max-w-5xl mx-auto space-y-8 pb-12" data-id="element-8881" data-mp="cltc466b5v6w">
        <ChildSelectorBar linkedLearners={linkedLearners} selectedChildId={selectedChild.id} onSelect={setViewingLearner} data-id="element-8882" data-mp="cj75c8r8rjpq" />
        

        <div className="flex items-center gap-4 mb-6" data-id="element-8883" data-mp="14by3wpwedki5">
          <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center border border-emerald-100 overflow-hidden flex-shrink-0" data-id="element-8884" data-mp="14ad70tmm1ph4">
            <DjayNarrator size="md" emotion="thinking" animate data-id="element-8885" data-mp="vagf8j4xcahu" />
          </div>
          <div className="flex-1" data-id="element-8886" data-mp="3l1d0orw1apfm">
            <h2 className="text-lg font-black text-slate-900 mb-1" data-id="element-8887" data-mp="3l2elho2uqqaj">
              Persoonlijke tips voor {selectedChild.displayName}
            </h2>
            <p className="text-sm text-slate-600" data-id="element-8888" data-mp="17tamvco2axc8">
              Ik heb het leergedrag geanalyseerd. Hier zijn mijn aanbevelingen!
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-8889" data-mp="1cllp4m69yv7y">
          <AnimatePresence initial={false} data-id="element-8890" data-mp="ytnoz0ujk9nx">
            {visibleTips.map((tip, i) => <motion.div key={tip.id} layout initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10,
            scale: 0.98
          }} transition={{
            delay: i * 0.05,
            duration: 0.25
          }} data-id="element-8891" data-mp="12t67jpyw48nl" data-mpi={i}>
              
                <Card className={`p-6 border h-full flex flex-col relative ${colorClasses[tip.color]} shadow-sm`} data-id="element-8892" data-mp="1mhx2lfhlpr3z">
                
                  {!tip.pinned && <button onClick={() => handleDismiss(tip)} aria-label={`Verberg tip: ${tip.title}`} title="Verberg deze tip (14 dagen)" className={`absolute top-3 right-3 w-7 h-7 rounded-lg flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-current ${dismissBtnClasses[tip.color]}`} data-id="element-8893" data-mp="62vtk5lavp1n">
                  
                      <X className="w-4 h-4" data-id="element-8894" data-mp="cim9znc1mbac" />
                    </button>}
                  <div className="flex items-center gap-4 mb-4 pr-8" data-id="element-8895" data-mp="7kc9qybgokhv">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBgClasses[tip.color]}`} data-id="element-8896" data-mp="3g0nlq7kms65s">
                    
                      {tip.icon}
                    </div>
                    <h3 className="text-lg font-black leading-tight" data-id="element-8897" data-mp="3pq78vpfdmm6g">
                      {tip.title}
                    </h3>
                  </div>
                  <p className="text-sm font-medium opacity-80 leading-relaxed flex-1" data-id="element-8898" data-mp="3kyfoxxqq7epa">
                    {tip.content}
                  </p>
                  {tip.action && <div className="mt-4 pt-4 border-t border-black/5" data-id="element-8899" data-mp="2sbuyczi2r6ms">
                      <button onClick={tip.action.onClick} className={`text-sm font-bold flex items-center gap-1 transition-colors ${actionTextClasses[tip.color]}`} data-id="element-8900" data-mp="w7yak6tjmolf">
                    
                        {tip.action.label}
                        <ArrowRight className="w-4 h-4" data-id="element-8901" data-mp="15xhvrj4qaujg" />
                      </button>
                    </div>}
                </Card>
              </motion.div>)}
          </AnimatePresence>
        </div>

        {visibleTips.length === 1 &&
      // Only the pinned platform-info card remains — everything else dismissed.
      <Card className="p-6 border border-dashed border-slate-200 bg-slate-50/60 text-center" data-id="element-8902" data-mp="i8yp181bupth">
            <p className="text-sm text-slate-600" data-id="element-8903" data-mp="m8zdzmm1yjes">
              Je hebt alle tips verborgen. Ze komen vanzelf weer terug zodra ze
              relevant zijn, of na 14 dagen.
            </p>
          </Card>}

        <Card className="p-8 border-slate-200 shadow-sm bg-white mt-8 text-center" data-id="element-8904" data-mp="1xoe72fb54r4m">
          <Lightbulb className="w-10 h-10 text-amber-400 mx-auto mb-4" data-id="element-8905" data-mp="22mpiguh339oy" />
          <h3 className="text-xl font-black text-slate-900 mb-2" data-id="element-8906" data-mp="2csgyzesppifn">
            Heb je vragen?
          </h3>
          {/*
           Hier stond: "De docent van X heeft ook toegang tot deze gegevens."
           Dat is een bewering over WIE er meekijkt, en het portaal weet dat
           niet — er is geen bron in de ouderomgeving die zegt of dit kind aan
           een klas is gekoppeld. Voor een kind zonder leerkracht is de zin
           onwaar; erger nog, hij suggereert inzage door iemand die er niet is.
           Precies het tegenovergestelde van wat we bij de koppelingen juist
           zorgvuldig zichtbaar maken.
            Wat blijft staan is wat wél waar is: ís er een leerkracht, dan kan
           die meer vertellen. Voorwaardelijk geformuleerd, zodat de ouder de
           vraag stelt in plaats van een aanname overneemt.
           */}
          <p className="text-slate-600 mb-6 max-w-lg mx-auto" data-id="element-8907" data-mp="3ef6xcoagyqwu">
            Gaat {selectedChild.displayName} naar school met een gekoppelde
            klas? Dan ziet de leerkracht deze voortgang ook en kan die er meer
            over vertellen. Wie er precies meekijkt, staat bij Instellingen.
          </p>
        </Card>
      </div>
    </PageLayout>;
}